/**
 * WHAT: Enterprise users report showing Azure AD user directory with filtering and statistics.
 * WHY:  Attestation and compliance workflows require visibility into the enterprise identity source
 *       (Azure AD) so reviewers can cross-reference database users with their organizational
 *       attributes such as department, job title, manager, and account enabled/disabled status.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import './EnterpriseUsersReport.css';

/**
 * WHAT: Renders a searchable, filterable table of all Azure AD enterprise users with summary statistics.
 * WHY:  Provides the identity baseline for correlating database accounts to real people, enabling
 *       compliance teams to verify user access matches organizational records and spot orphaned accounts.
 */
const EnterpriseUsersReport = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    jobTitle: '',
    accountStatus: 'all',
    officeLocation: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    departments: [],
    jobTitles: [],
    locations: []
  });
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'display_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Using useCallback to memoize the fetch function
  const fetchEnterpriseUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all users by following pagination
      let allUsers = [];
      let nextUrl = '/api/enterprise-users/';

      while (nextUrl) {
        const response = await api.enterpriseUsers.getAll(nextUrl);
        const pageData = response.data.results || [];
        allUsers = [...allUsers, ...pageData];

        // Check if there's a next page
        nextUrl = response.data.next;
        if (nextUrl) {
          // Extract just the path and query from the full URL
          const url = new URL(nextUrl);
          nextUrl = url.pathname + url.search;
        }
      }

      if (allUsers.length > 0) {
        setUsers(allUsers);

        // Calculate statistics
        const departments = [...new Set(allUsers.map(u => u.department).filter(Boolean))].sort();
        const jobTitles = [...new Set(allUsers.map(u => u.job_title).filter(Boolean))].sort();
        const locations = [...new Set(allUsers.map(u => u.office_location).filter(Boolean))].sort();

        setStats({
          total: allUsers.length,
          active: allUsers.filter(u => u.account_enabled).length,
          inactive: allUsers.filter(u => !u.account_enabled).length,
          departments,
          jobTitles,
          locations
        });
      } else {
        setError('No enterprise users found');
      }
    } catch (err) {
      console.error('Error fetching enterprise users:', err);
      // Better error handling with the API service
      setError(err.response?.data?.error || err.message || 'Failed to fetch enterprise users');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array since function doesn't depend on any props/state

  useEffect(() => {
    fetchEnterpriseUsers();
  }, [fetchEnterpriseUsers]);

  // Using useCallback to prevent unnecessary re-renders
  const applyFilters = useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        (user.display_name || '').toLowerCase().includes(search) ||
        (user.email || '').toLowerCase().includes(search) ||
        (user.user_principal_name || '').toLowerCase().includes(search) ||
        (user.employee_id || '').toLowerCase().includes(search)
      );
    }

    // Department filter
    if (filters.department) {
      filtered = filtered.filter(user => user.department === filters.department);
    }

    // Job Title filter
    if (filters.jobTitle) {
      filtered = filtered.filter(user => user.job_title === filters.jobTitle);
    }

    // Office Location filter
    if (filters.officeLocation) {
      filtered = filtered.filter(user => user.office_location === filters.officeLocation);
    }

    // Account Status filter
    if (filters.accountStatus === 'active') {
      filtered = filtered.filter(user => user.account_enabled);
    } else if (filters.accountStatus === 'inactive') {
      filtered = filtered.filter(user => !user.account_enabled);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, filters, sortConfig]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      department: '',
      jobTitle: '',
      accountStatus: 'all',
      officeLocation: ''
    });
    setCurrentPage(1);
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, sortConfig]);

  const exportToCSV = () => {
    const headers = ['Employee ID', 'User ID', 'Display Name', 'Email', 'Job Title', 'Department', 'Manager ID', 'Manager Name', 'Account Enabled'];
    const csvData = filteredUsers.map(user => [
      user.employee_id || '',
      user.user_id || '',
      user.display_name || '',
      user.email || '',
      user.job_title || '',
      user.department || '',
      user.manager_id || '',
      user.manager_name || '',
      user.account_enabled ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `enterprise_users_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="fas fa-sort ms-2 text-muted"></i>;
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up ms-2"></i>
      : <i className="fas fa-sort-down ms-2"></i>;
  };

  if (loading) {
    return (
      <div className="enterprise-users-report">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading enterprise users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enterprise-users-report">
        <div className="alert alert-danger m-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-users-report">
      <div className="report-header">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-0">
              <i className="fas fa-users me-2"></i>
              Enterprise Users Report
            </h2>
            <p className="text-muted mb-0">View and manage enterprise user directory</p>
          </div>
          <button
            className="btn btn-success"
            onClick={exportToCSV}
            disabled={filteredUsers.length === 0}
          >
            <i className="fas fa-download me-2"></i>
            Export to CSV
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="stat-card">
              <div className="stat-icon bg-primary">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.total.toLocaleString()}</div>
                <div className="stat-label">Total Users</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card">
              <div className="stat-icon bg-success">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.active.toLocaleString()}</div>
                <div className="stat-label">Active Users</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card">
              <div className="stat-icon bg-danger">
                <i className="fas fa-ban"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.inactive.toLocaleString()}</div>
                <div className="stat-label">Inactive Users</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card">
              <div className="stat-icon bg-info">
                <i className="fas fa-building"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.departments.length}</div>
                <div className="stat-label">Departments</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section mb-4">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Search</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Name, email, ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">Department</label>
              <select
                className="form-select"
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
              >
                <option value="">All Departments</option>
                {stats.departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Job Title</label>
              <select
                className="form-select"
                value={filters.jobTitle}
                onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
              >
                <option value="">All Job Titles</option>
                {stats.jobTitles.map(title => (
                  <option key={title} value={title}>{title}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Account Status</label>
              <select
                className="form-select"
                value={filters.accountStatus}
                onChange={(e) => handleFilterChange('accountStatus', e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">&nbsp;</label>
              <button
                className="btn btn-outline-secondary w-100"
                onClick={resetFilters}
              >
                <i className="fas fa-redo me-2"></i>
                Reset
              </button>
            </div>
          </div>
          <div className="mt-2 text-muted">
            Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} users
            {filteredUsers.length !== users.length && ` (filtered from ${users.length} total)`}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table className="table table-hover">
          <thead className="table-dark">
            <tr>
              <th onClick={() => handleSort('employee_id')} style={{ cursor: 'pointer' }}>
                Employee ID {getSortIcon('employee_id')}
              </th>
              <th onClick={() => handleSort('display_name')} style={{ cursor: 'pointer' }}>
                Display Name {getSortIcon('display_name')}
              </th>
              <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                Email {getSortIcon('email')}
              </th>
              <th onClick={() => handleSort('job_title')} style={{ cursor: 'pointer' }}>
                Job Title {getSortIcon('job_title')}
              </th>
              <th onClick={() => handleSort('department')} style={{ cursor: 'pointer' }}>
                Department {getSortIcon('department')}
              </th>
              <th onClick={() => handleSort('manager_id')} style={{ cursor: 'pointer' }}>
                Manager ID {getSortIcon('manager_id')}
              </th>
              <th onClick={() => handleSort('manager_name')} style={{ cursor: 'pointer' }}>
                Manager Name {getSortIcon('manager_name')}
              </th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-5 text-muted">
                  <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                  No users found matching your filters
                </td>
              </tr>
            ) : (
              currentUsers.map((user) => (
                <tr key={user.user_id}>
                  <td>{user.employee_id || 'N/A'}</td>
                  <td>
                    <strong>{user.display_name || 'N/A'}</strong>
                    <br />
                    <small className="text-muted">{user.user_principal_name || user.user_id}</small>
                  </td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{user.job_title || 'N/A'}</td>
                  <td>{user.department || 'N/A'}</td>
                  <td>{user.manager_id || 'N/A'}</td>
                  <td>{user.manager_name || 'N/A'}</td>
                  <td>
                    {user.account_enabled ? (
                      <span className="badge bg-success">
                        <i className="fas fa-check-circle me-1"></i>
                        Active
                      </span>
                    ) : (
                      <span className="badge bg-danger">
                        <i className="fas fa-ban me-1"></i>
                        Inactive
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredUsers.length > 0 && (
        <div className="pagination-container mt-4">
          <div className="d-flex justify-content-between align-items-center">
            {/* Items per page selector */}
            <div className="d-flex align-items-center gap-2">
              <label className="text-white mb-0">Show:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-white">per page</span>
            </div>

            {/* Page navigation */}
            <nav>
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>
                </li>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }

                  return (
                    <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    </li>
                  );
                })}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>

            {/* Page info */}
            <div className="text-white">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseUsersReport;
